import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFavoritesContext } from '../context/FavoritesContext';
import { useAuth } from '../context/AuthContext';
import StopDetailsTabs from './StopDetailsTabs';
import FavoriteButton from './FavoriteButton';
import { trackEvent } from '../utils/analytics';
import './BusStopList.css';

const BusStopList = ({ busStops, language = 'tr', searchQuery = '', defaultStops = [], onClearSearch }) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const {
    favorites,
    isFavorite,
    getFavorite,
    getFavoriteLabel,
    addFavorite,
    removeFavorite,
    updateFavorite,
    setFavoritesOrder,
    reorderFavorites
  } = useFavoritesContext();
  const [expandedStopId, setExpandedStopId] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [editingLabelId, setEditingLabelId] = useState(null);
  const [editLabelValue, setEditLabelValue] = useState('');

  // Touch drag state
  const [touchDraggedItem, setTouchDraggedItem] = useState(null);
  const [touchDragOverIndex, setTouchDragOverIndex] = useState(null);
  const touchStarted = useRef(false);
  const rowRefs = useRef({});

  // Determine which stops to display
  const displayStops = useMemo(() => {
    // If there's a search query, show search results
    if (searchQuery && busStops.length > 0) {
      // Add searched stop first, then favorites that are not in search results
      const searchedIds = new Set(busStops.map(s => s.id));
      const favoriteStopsNotInSearch = favorites
        .filter(f => !searchedIds.has(f.busStopId))
        .map(f => f.busStop)
        .filter(Boolean);

      return [...busStops, ...favoriteStopsNotInSearch];
    }

    // If user has favorites, show only favorites in their order
    if (favorites.length > 0) {
      return favorites
        .map(f => f.busStop)
        .filter(Boolean);
    }

    // Otherwise show default stops from DB
    return defaultStops;
  }, [busStops, favorites, searchQuery, defaultStops]);

  const handleRowClick = (stopId) => {
    trackEvent('stop_details_toggled', {
      stop_id: stopId,
      expanded: expandedStopId !== stopId
    });
    setExpandedStopId(expandedStopId === stopId ? null : stopId);
  };

  const handleFavoriteToggle = async (stop, label) => {
    if (!isAuthenticated) return;

    const favorite = getFavorite(stop.id);
    if (favorite) {
      if (!window.confirm(t('favorites.confirmDelete'))) {
        return;
      }
      await removeFavorite(favorite.id);
      trackEvent('favorite_removed', {
        stop_number: stop.stopNumber
      });
    } else {
      await addFavorite(stop.id, label || '', stop);
      trackEvent('favorite_added', {
        stop_number: stop.stopNumber,
        has_custom_label: Boolean(label && label.trim())
      });
      // Clear search after adding to favorites so user can reorder
      if (onClearSearch) {
        onClearSearch();
      }
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, index, stop) => {
    if (!isFavorite(stop.id)) {
      e.preventDefault();
      return;
    }
    setDraggedItem({ index, stop });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', stop.id.toString());
    e.currentTarget.classList.add('dragging');
  };

  const handleDragEnd = (e) => {
    e.currentTarget.classList.remove('dragging');
    setDraggedItem(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = useCallback(async (e, dropIndex) => {
    e.preventDefault();
    if (!draggedItem) return;

    const dragIndex = favorites.findIndex(f => f.busStopId === draggedItem.stop.id);
    const targetStop = displayStops[dropIndex];
    const targetIndex = favorites.findIndex(f => f.busStopId === targetStop?.id);

    if (dragIndex === -1 || targetIndex === -1 || dragIndex === targetIndex) return;

    // Reorder favorites locally first for immediate feedback
    const newFavorites = [...favorites];
    const [removed] = newFavorites.splice(dragIndex, 1);
    newFavorites.splice(targetIndex, 0, removed);
    setFavoritesOrder(newFavorites);

    // Then save to server
    try {
      const orderedIds = newFavorites.map(f => f.id);
      await reorderFavorites(orderedIds);
      trackEvent('favorites_reordered', {
        total_favorites: newFavorites.length
      });
    } catch (err) {
      // Revert on error
      setFavoritesOrder(favorites);
    }
  }, [draggedItem, favorites, displayStops, setFavoritesOrder, reorderFavorites]);

  // Touch drag handlers for mobile
  const handleDragHandleTouchStart = useCallback((e, index, stop) => {
    if (!isFavorite(stop.id)) return;

    e.preventDefault();
    e.stopPropagation();

    touchStarted.current = true;
    setTouchDraggedItem({ index, stop });
    rowRefs.current[stop.id]?.classList.add('dragging');
  }, [isFavorite]);

  // Global touch move/end handlers
  useEffect(() => {
    if (!touchDraggedItem) return;

    const handleGlobalTouchMove = (e) => {
      e.preventDefault();
      const touch = e.touches[0];

      // Find which row we're over
      const favoriteStops = displayStops.filter(s => isFavorite(s.id));
      for (let i = 0; i < favoriteStops.length; i++) {
        const stop = favoriteStops[i];
        const row = rowRefs.current[stop.id];
        if (row) {
          const rect = row.getBoundingClientRect();
          if (touch.clientY >= rect.top && touch.clientY <= rect.bottom) {
            const displayIndex = displayStops.findIndex(s => s.id === stop.id);
            setTouchDragOverIndex(displayIndex);
            return;
          }
        }
      }
    };

    const handleGlobalTouchEnd = async () => {
      touchStarted.current = false;

      // Clean up dragging class
      Object.values(rowRefs.current).forEach(row => {
        row?.classList.remove('dragging');
      });

      if (touchDragOverIndex === null) {
        setTouchDraggedItem(null);
        setTouchDragOverIndex(null);
        return;
      }

      const dragIndex = favorites.findIndex(f => f.busStopId === touchDraggedItem.stop.id);
      const targetStop = displayStops[touchDragOverIndex];
      const targetIndex = favorites.findIndex(f => f.busStopId === targetStop?.id);

      if (dragIndex === -1 || targetIndex === -1 || dragIndex === targetIndex) {
        setTouchDraggedItem(null);
        setTouchDragOverIndex(null);
        return;
      }

      // Reorder favorites locally first
      const newFavorites = [...favorites];
      const [removed] = newFavorites.splice(dragIndex, 1);
      newFavorites.splice(targetIndex, 0, removed);
      setFavoritesOrder(newFavorites);

      // Save to server
      try {
        const orderedIds = newFavorites.map(f => f.id);
        await reorderFavorites(orderedIds);
        trackEvent('favorites_reordered', {
          total_favorites: newFavorites.length
        });
      } catch (err) {
        setFavoritesOrder(favorites);
      }

      setTouchDraggedItem(null);
      setTouchDragOverIndex(null);
    };

    document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
    document.addEventListener('touchend', handleGlobalTouchEnd);
    document.addEventListener('touchcancel', handleGlobalTouchEnd);

    return () => {
      document.removeEventListener('touchmove', handleGlobalTouchMove);
      document.removeEventListener('touchend', handleGlobalTouchEnd);
      document.removeEventListener('touchcancel', handleGlobalTouchEnd);
    };
  }, [touchDraggedItem, touchDragOverIndex, favorites, displayStops, isFavorite, setFavoritesOrder, reorderFavorites]);

  // Label editing handlers
  const handleEditLabel = (e, stop) => {
    e.stopPropagation();
    const favorite = getFavorite(stop.id);
    if (favorite) {
      setEditingLabelId(stop.id);
      setEditLabelValue(favorite.label || '');
    }
  };

  const handleSaveLabel = async (e, stop) => {
    e.stopPropagation();
    const favorite = getFavorite(stop.id);
    if (favorite) {
      await updateFavorite(favorite.id, { label: editLabelValue });
      trackEvent('favorite_label_updated', {
        stop_number: stop.stopNumber
      });
    }
    setEditingLabelId(null);
    setEditLabelValue('');
  };

  const handleCancelEditLabel = (e) => {
    e.stopPropagation();
    setEditingLabelId(null);
    setEditLabelValue('');
  };

  // Get display text for address column (always Turkish names)
  const getDisplayAddress = (stop) => {
    const favorite = getFavorite(stop.id);
    if (favorite?.label) {
      return favorite.label;
    }
    return stop.address;
  };

  if (displayStops.length === 0 && !searchQuery) {
    return (
      <div className="empty-state">
        <p>{favorites.length === 0 ? t('busStopList.empty') : t('favorites.empty')}</p>
      </div>
    );
  }

  if (displayStops.length === 0 && searchQuery) {
    return (
      <div className="empty-state">
        <p>{t('search.noResults')}</p>
      </div>
    );
  }

  return (
    <div className="bus-stop-list">
      {favorites.length > 0 && !searchQuery && (
        <div className="favorites-header">
          <span className="favorites-title">{t('favorites.title')}</span>
          <span className="drag-hint">{t('favorites.dragHint')}</span>
        </div>
      )}
      <table className={`bus-stop-table ${!isAuthenticated ? 'guest-view' : ''}`}>
        <thead>
          <tr>
            {isAuthenticated && <th className="favorite-column"></th>}
            <th className="stop-number-column">{t('busStopList.stopNumber')}</th>
            <th>{t('busStopList.address')}</th>
            <th className="expand-column"></th>
          </tr>
        </thead>
        <tbody>
          {displayStops.map((stop, index) => (
            <React.Fragment key={stop.id}>
              <tr
                ref={(el) => { rowRefs.current[stop.id] = el; }}
                className={`bus-stop-row ${expandedStopId === stop.id ? 'expanded' : ''} ${isFavorite(stop.id) ? 'favorite' : ''} ${draggedItem?.stop.id === stop.id || touchDraggedItem?.stop.id === stop.id ? 'dragging' : ''} ${touchDragOverIndex === index && touchDraggedItem ? 'drag-over' : ''}`}
                onClick={() => handleRowClick(stop.id)}
                draggable={isFavorite(stop.id)}
                onDragStart={(e) => handleDragStart(e, index, stop)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDrop(e, index)}
              >
                {isAuthenticated && (
                  <td className="favorite-cell" onClick={(e) => e.stopPropagation()}>
                    <FavoriteButton
                      busStop={stop}
                      isFavorite={isFavorite(stop.id)}
                      onToggle={(label) => handleFavoriteToggle(stop, label)}
                      showLabel={true}
                    />
                  </td>
                )}
                <td className="stop-number-cell">
                  {isFavorite(stop.id) && (
                    <span
                      className="drag-handle"
                      title={t('favorites.dragHint')}
                      onTouchStart={(e) => handleDragHandleTouchStart(e, index, stop)}
                    >
                      ⋮⋮
                    </span>
                  )}
                  {stop.stopNumber}
                </td>
                <td className="address-cell">
                  {editingLabelId === stop.id ? (
                    <div className="label-edit-inline" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="text"
                        value={editLabelValue}
                        onChange={(e) => setEditLabelValue(e.target.value)}
                        placeholder={t('favorites.labelPlaceholder')}
                        className="label-input-inline"
                        autoFocus
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') handleSaveLabel(e, stop);
                        }}
                      />
                      <button onClick={(e) => handleSaveLabel(e, stop)} className="btn-save-inline">✓</button>
                      <button onClick={handleCancelEditLabel} className="btn-cancel-inline">✕</button>
                    </div>
                  ) : (
                    <>
                      <span className={isFavorite(stop.id) && getFavoriteLabel(stop.id) ? 'custom-label' : ''}>
                        {getDisplayAddress(stop)}
                      </span>
                      {isFavorite(stop.id) && (
                        <button
                          className="btn-edit-label"
                          onClick={(e) => handleEditLabel(e, stop)}
                          title={t('favorites.edit')}
                        >
                          ✎
                        </button>
                      )}
                    </>
                  )}
                </td>
                <td className="expand-cell">
                  <button className="btn-expand" aria-label={t('busStopList.expand')}>
                    {expandedStopId === stop.id ? '▲' : '▼'}
                  </button>
                </td>
              </tr>
              {expandedStopId === stop.id && (
                <tr className="expanded-row">
                  <td colSpan={isAuthenticated ? 4 : 3}>
                    <div className="stop-details">
                      <div className="stop-details-content">
                        <StopDetailsTabs stopNumber={stop.stopNumber} />
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>

    </div>
  );
};

export default BusStopList;
